import { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../../../../constants';

/**
 * useSkillSearch
 * Strategy: Load ALL skills ONCE on mount, then filter locally on every keystroke.
 * Zero API calls during typing — filtering happens in memory only.
 *
 * Returns: { allSkills, results, loading, error, query, setQuery }
 */
const useSkillSearch = () => {
    const [allSkills, setAllSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');

    // Fetch once on mount — same endpoint as Skills.js
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/skills`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setAllSkills(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []); // Only once — no refetch on keystroke

    // Filter locally — instant, no network
    const results = useMemo(() => {
        if (!query.trim()) return [];
        const lower = query.toLowerCase();
        return allSkills
            .filter(s => s.Skill_name?.toLowerCase().includes(lower))
            .slice(0, 30);
    }, [allSkills, query]);

    return { results, loading, error, query, setQuery };
};

export default useSkillSearch;
